/**
 * The main component that renders single TabComponent
 * instances.
 */

import {
	Component,
	ContentChildren,
	EventEmitter,
	Input,
	Output,
	QueryList,
	AfterContentInit,
} from "@angular/core";

import { TabComponent } from "../tab-component/tab.component";

@Component({
    selector: "my-tabs",
    templateUrl: "./tabs.component.html",
    styleUrls: ["./tabs.component.scss"],
    standalone: false
})
export class TabsComponent implements AfterContentInit {
	@ContentChildren(TabComponent) tabs: QueryList<TabComponent>;

	/**
	 * Optional localStorage key. When set, the last selected tab title
	 * is persisted under this key so the tab survives a re-mount (e.g.
	 * Manage > Series, where doing an action on a non-Video tab used to
	 * snap the user back to Video Series after the sub-route returned).
	 */
	@Input() storageKey?: string;

	/** Emits the active tab's index whenever the active tab changes. */
	@Output() tabChange = new EventEmitter<number>();

	ngAfterContentInit() {
		let activeTabs = this.tabs.filter((tab) => tab.active);

		if (activeTabs.length === 0) {
			const remembered = this.readRemembered();
			const match = remembered
				? this.tabs.find((tab) => tab.title === remembered)
				: null;
			this.selectTab(match ?? this.tabs.first);
		}
	}

	selectTab(tab: TabComponent) {
		const tabArr = this.tabs.toArray();
		tabArr.forEach((t) => (t.active = false));
		tab.active = true;
		this.writeRemembered(tab.title);
		this.tabChange.emit(tabArr.indexOf(tab));
	}

	private readRemembered(): string | null {
		if (!this.storageKey) {
			return null;
		}
		try {
			return localStorage.getItem(this.storageKey);
		} catch {
			return null;
		}
	}

	private writeRemembered(title: string): void {
		if (!this.storageKey || !title) {
			return;
		}
		try {
			localStorage.setItem(this.storageKey, title);
		} catch {
			/* ignore: localStorage unavailable / quota exceeded */
		}
	}
}
